import { Component, OnInit, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { SppdashboardService } from 'src/app/view/dashboard/sppdashboard.service';
import { UserPaymentModalComponent } from 'src/app/component/user-payment-modal/user-payment-modal.component';
import { TopazDialogMaxSize } from '@pearsonvue/topaz-angular-ui';

@Injectable({
  providedIn: 'root'
})

@Component({
  selector: 'app-live',
  templateUrl: './live.component.html',
  standalone: false,
  styleUrl: './live.component.scss'
})
export class LiveComponent {

  userid: any;
  countryCode: any;
  status: any;
  paymentlink: any;
  countryList: any;
  private previousUrl: string | null;


  constructor(
    private readonly router: Router,
    private readonly sppdashboardService: SppdashboardService,
    private readonly dialogModel: MatDialog, 
    private readonly location: Location) {
      this.previousUrl = this.router.getCurrentNavigation()?.previousNavigation?.finalUrl?.toString() || null;
    }

  public _currentLink = new BehaviorSubject<string | null>(null); // Initial value is null

  public currentLink$: Observable<string | null> = this._currentLink.asObservable();
  private linkSource = new BehaviorSubject<string>('default');
  currentLink = this.linkSource.asObservable();

  changeLink(message: any) {
    this.linkSource.next(message);
  }

  get currentLinkValue(): string {
    return this.linkSource.value;
  }

  async ngOnInit() {
   await  this.getCountryList();

    this.sppdashboardService.getUser().subscribe(async (response: any) => {
      this.userid = response.data.userid;
      this.countryCode = this.countryList?.find((country: any) => country.countryName === response.data.country)?.countryCode || null;
      this.status = response.data.status;
      await this.getPaymentLink();
      this.navigationParamMapping(response.data.status);
    });
  }

  navigationParamMapping = (status: string): void => {
    if(this.previousUrl?.includes('/reinstatement-mct')){
        this.router.navigate(['/home']);
        return;
    }
    const stateToPass = this.previousUrl?.includes('/dashboard') && (status === 'ILT_OPEN' || status === 'TEST_OUT_FAIL' || status === 'ILT_FAIL') ? { skipForm: true } : {};
    this.navigateToPreviousRoute(stateToPass);
  }

  private navigateToPreviousRoute(navigationState: Record<string, any> = {}): void {
    if (this.previousUrl) {
      const urlTree = this.router.parseUrl(this.previousUrl);
      this.router.navigateByUrl(urlTree, { state: navigationState });
      return;
    }

    if (window.history.length > 1) {
      this.location.back();
      return;
    }

    this.router.navigate(['/home'], { state: navigationState });
  }

  public openUserPaymentModal(data: any): void {
    const maxWidth = data.isLinkAvailable
      ? TopazDialogMaxSize.MEDIUM
      : TopazDialogMaxSize.SMALL;
    const dialogRef = this.dialogModel.open(UserPaymentModalComponent, {
      maxWidth: maxWidth,
      data: { ...data, hideCloseIcon: true },
      id: 'smalldialog',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((value) => {
      if (value === 'conactSupport') {
        this.router.navigateByUrl('/support');
      }
      if (value === 'paymentLink') {
        if (this.isSafeUrl(this.paymentlink || '')) {
          window.open(this.paymentlink || '', '_blank', 'noopener,noreferrer');
        } else {
          console.warn('Blocked untrusted redirect target:', this.paymentlink);
          // optionally show a user-facing error/toast
        }
      }
    })
  }

  private readonly allowedHosts = ['mozu.com', 'pearsonvue.eu', 'pearsonvue.com','pearsonvue.co.uk', 'pearsonvue.co.jp', 'pearsonvue.com.au'];

  private isSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return this.allowedHosts.some(host =>
      parsed.hostname === host || parsed.hostname.endsWith(`.${host}`)
    );
  } catch {
    return false;
  }
}
  public async getPaymentLink() {
    if (!this.countryCode) {
      this.openUserPaymentModal({
        title: 'Subscription payment unavailable',
        description: 'We cannot find the country code, Please try again.',
        isLinkAvailable: false,
      });
      return;
    }

    this.sppdashboardService.payLink(this.countryCode, this.status).subscribe((response: any) => {
      if (response.success === true) {

        let modalText = 'You are now being redirected to our secure e-commerce portal to complete your MCT Program fee payment. Once your payment is successfully processed, you will be able to access program benefits.';
        if(this.status === 'OPEN' ){
        modalText = 'You are being redirected to our secure e‑commerce portal to complete the ILT training and subscription payment. Upon successful payment, your training will be enrolled and available to proceed.';
        } else if(this.status === 'TEST_OUT_OPEN'){
        modalText = 'You are now being redirected to our secure e-commerce portal to complete your MCT Instructional Skills Assessment payment. Once your payment is successfully processed, a link to the assessment will become available.';
        } else if(this.status === 'ILT_OPEN' || this.status === 'ILT_FAIL' || this.status === 'TEST_OUT_FAIL'){
          modalText =  'You are now being redirected to our secure e-commerce portal to complete payment for your MCT Onboarding Experience course. Upon successful completion of the course, you will automatically receive a complimentary 1-year subscription to the MCT Program — no additional action or payment is required. Once complete, your MCT status will become "Active," providing access to all MCT Program benefits, resources, and privileges.'
        } else {
          modalText = 'You are now being redirected to our secure e-commerce portal to complete your annual MCT Program Fee payment.';
        }


        this.paymentlink = response.data;
        const data = {
          title: '',
          description: modalText,
          isLinkAvailable: response.success,
        }
        this.openUserPaymentModal(data);
      }
      else if (response.success === false) {
        const data = {
          title: 'Subscription payment unavailable',
          description: 'We cannot support payment in your country at this time.',
          isLinkAvailable: response.success,
        }
        this.openUserPaymentModal(data);
      }
    })
  }

  getCountryList = async (): Promise<void> => {
    try {
      this.countryList = await firstValueFrom(this.sppdashboardService.getCountryList());
    } catch (error: any) {
      console.error('Error fetching Country List', error);
      this.countryList = [];
    }
  }

}
